import { DateTime } from 'luxon';
import type { AvailableTimeType } from '../../types/baseTypes/DateRangeType';
import {
  DaysInWeek,
  TimeAvailabilityFeatureProperties,
  WeeklyAvailability,
} from '../../types/features/TimeAvailabilityFeatureProperties';

/**
 * This provides an array of available time in a day in minutes. It takes in the `TimeAvailabilityFeatureProperties` feature
 *
 * @param {Date | undefined} referenceDate - This is the date that is referenced. The data needed from it is only the day of the week, e.g "MONDAY". 
 * It must be in reference to the channel's timezone for proper calculation,
 * @param {TimeAvailabilityFeatureProperties | null | undefined} timeAvailabilityFeature - The TimeAvailabilityFeatureProperties object which contains 
 * data about the channel's time availability for that interaction
 * @returns {AvailableDateType[]} A range of available time ranges, in minutes, from the start of the day. 
 *
* @example
 * // Sends back a list of available time in minutes: "
 * const data = getAvailableTimeRangesFromTimeAvailability(date, timeAvailabilityFeature);
 * console.log(data)
 * 
 * // log output
 * [{
 *    startTime:480,
 *    endTime:720,
 * },
 * {
 *    startTime:1020,
 *    endTime:1400,
 * }]
 * 
 */
export default function getAvailableTimeRangesFromTimeAvailability(
  referenceDate: Date | undefined,
  timeAvailabilityFeature: TimeAvailabilityFeatureProperties | null | undefined
): AvailableTimeType[] {
  if (!referenceDate) {
    return [];
  }

  const day = DateTime.fromJSDate(referenceDate)
    .toFormat('cccc')
    .toUpperCase() as DaysInWeek;
  const availability: WeeklyAvailability | undefined =
    timeAvailabilityFeature?.availabilities?.[0];

  if (!availability || availability?.isAvailableAnytime) {
    return [];
  }

  const currentRule = availability?.weekTimeRules?.[day];

  if (!currentRule || !currentRule.isOpen || currentRule.isAvailableAllDay) {
    return [];
  }

  const ranges = currentRule.timeRanges.map(item => ({
    startTime: timeStringToMinutes(item.startTime),
    endTime: timeStringToMinutes(item.endTime),
  }));

  return ranges;
}


/**
 * Converts a string to it's equivalent time in minutes from start of day
 *
 * @param {string} timeStr - The time to convert to minutes. An example of the required format is `2:00 AM`, `5:00 PM`, etc
 * @returns {number} The number representing minutes from the start
 *
* @example
 * // Converts 2:00 AM to the right time in minutes
 * const result = timeStringToMinutes("2:00 AM");
 * // result === 120
 */
function timeStringToMinutes(timeStr: string): number {
  const dt = DateTime.fromFormat(timeStr, 'h:mm a');

  return dt.hour * 60 + dt.minute;
}
